/**
 * FRONT-907 / FRONT-1037: the app shell's two onboarding gates, plus the
 * dev-only switch that turns both off.
 *
 * Gate 1, the wizard: a user with no identity yet (no self-entity in the
 * graph) is sent to `/onboard`, where the first step asks for a name.
 * Gate 2, the day-one chat: an onboarded user whose day-one chat is still
 * `pending` (reload mid-conversation, tab close) is sent back into it.
 *
 * Both are right for a real first-time user and a nuisance on a local
 * stack, where every fresh account starts at the wizard, the chat flag is
 * always on, and a developer testing something else reloads constantly.
 *
 * The switch: in a DEV build only, `?skip-onboarding=1` on any app-shell
 * page (not `/onboard` itself) remembers, in this browser, to skip both.
 * Instead of the wizard, the shell does the wizard's writes itself (see
 * `features/onboard/dev-auto-onboard.ts`); the day-one chat is then never
 * seeded, and a pending one never bounces.
 * `?skip-onboarding=0` forgets the switch (the stored name stays), and
 * `/onboard?replay=1` still replays the whole flow. A production build never reads the param or the stored
 * value, so real users always get both gates.
 */

export const DEV_SKIP_ONBOARDING_PARAM = 'skip-onboarding';
export const DEV_SKIP_ONBOARDING_KEY = 'eddy.dev.skip-onboarding';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Whether the dev switch is on for this load. Applies the URL param first
 * (`1` stores, `0` clears), then reads the stored value. `getStorage` is a
 * function because merely touching `window.localStorage` throws in some
 * privacy modes; any storage failure falls back to the param alone.
 */
export function resolveDevSkipOnboarding(input: {
  isDev: boolean;
  search: string;
  getStorage: () => StorageLike;
}): boolean {
  if (!input.isDev) return false;
  const param = new URLSearchParams(input.search).get(DEV_SKIP_ONBOARDING_PARAM);
  try {
    const storage = input.getStorage();
    if (param === '1') storage.setItem(DEV_SKIP_ONBOARDING_KEY, '1');
    if (param === '0') storage.removeItem(DEV_SKIP_ONBOARDING_KEY);
    return storage.getItem(DEV_SKIP_ONBOARDING_KEY) === '1';
  } catch {
    return param === '1';
  }
}

/** Whether this app load should hard-redirect into the day-one chat. */
export function shouldResumeDayOneChat(input: {
  authed: boolean;
  onboarded: boolean | undefined;
  sessionId: string | null | undefined;
  chatStatus: string | null | undefined;
  pathname: string;
  devSkip: boolean;
}): boolean {
  if (input.devSkip) return false;
  if (!input.authed || input.onboarded !== true) return false;
  if (!input.sessionId || input.chatStatus !== 'pending') return false;
  return !input.pathname.startsWith('/onboard');
}

export type OnboardedGate = 'stay' | 'wizard' | 'dev-auto-onboard';

/**
 * What the shell does about a user who has no identity yet: nothing while
 * the answer is unknown, the wizard normally, or (dev switch on) fill the
 * identity in itself. Already on `/onboard` means the wizard is showing.
 */
export function decideOnboardedGate(input: {
  authed: boolean;
  onboardedReady: boolean;
  onboarded: boolean | undefined;
  pathname: string;
  devSkip: boolean;
}): OnboardedGate {
  if (!input.authed || !input.onboardedReady) return 'stay';
  if (input.onboarded !== false) return 'stay';
  if (input.pathname.startsWith('/onboard')) return 'stay';
  return input.devSkip ? 'dev-auto-onboard' : 'wizard';
}

/**
 * The name the dev skip stores in place of the wizard's first step: the
 * sign-in name, else the part of the email before the @, else a fixed word.
 * Never empty, because the identity endpoint refuses an empty name.
 */
export function devIdentityName(me: { name?: string; email?: string } | undefined): string {
  const name = me?.name?.trim();
  if (name) return name;
  const local = me?.email?.trim().split('@')[0]?.trim();
  if (local) return local;
  return 'Developer';
}
